import { normalizeCode } from './codes'

/**
 * Fysiska lappar.
 *
 * Första upplagan trycks på papper. Lapparna delas ut på en träning, och
 * registreringen är det som knyter en lapp till den familj som håller den –
 * underlaget för vem som ska lämna in vad till lagkassan.
 *
 * Att registrera är inte att aktivera, och skillnaden är hela poängen:
 *
 *   Registrera  – föräldern säger "de här lapparna har jag och ska sälja".
 *                 Häftena är fortfarande oanvända och går att sälja vidare.
 *   Aktivera    – köparen öppnar sitt häfte. Koden är förbrukad som säljvara
 *                 och hör från och med då till den som aktiverade.
 *
 * En förälder som råkar aktivera sina fyra lappar har bränt dem. Därför
 * vägrar registreringen ta emot en redan aktiverad kod, och därför heter
 * knapparna och texterna olika saker hela vägen genom appen.
 */

/** Så många lappar går att registrera i ett svep. */
export const MAX_SLIPS = 50

export type SlipOutcome =
  | 'registered'
  | 'alreadyYours'
  | 'takenByOther'
  | 'activated'
  | 'wrongTeam'
  | 'unknown'

export interface SlipResult {
  code: string
  outcome: SlipOutcome
}

/** Kort etikett i listan efter registreringen. */
export const SLIP_OUTCOME_LABELS: Record<SlipOutcome, string> = {
  registered: 'Registrerad',
  alreadyYours: 'Redan din',
  takenByOther: 'Upptagen',
  activated: 'Redan aktiverad',
  wrongTeam: 'Annat lag',
  unknown: 'Okänd kod',
}

/**
 * Vad som gick fel, i klartext.
 *
 * Varje rad ska gå att läsa av en förälder som står med lappen i handen och
 * undrar vad hen ska göra nu. Därför står det vad man gör åt saken, inte bara
 * vad som hände.
 */
export const SLIP_OUTCOME_HELP: Record<SlipOutcome, string> = {
  registered: 'Häftet ligger nu bland dina.',
  alreadyYours: 'Den här lappen har du redan registrerat. Inget mer behöver göras.',
  takenByOther:
    'Någon annan har redan registrerat den här lappen. Kolla att du läst av koden rätt – ' +
    'är den rätt, hör av dig till lagledaren.',
  activated:
    'Häftet är redan aktiverat av en köpare och går inte att registrera. Har du inte sålt ' +
    'det själv, prata med lagledaren.',
  wrongTeam: 'Lappen hör till ett annat lag. Lagledaren kan bara registrera sitt eget lags lappar.',
  unknown: 'Vi hittar ingen kod med de tecknen. Kontrollera att du fått med alla tecken.',
}

/** Utfall som betyder att lappen ligger rätt, oavsett om vi gjorde jobbet nu. */
export function isSlipSettled(outcome: SlipOutcome): boolean {
  return outcome === 'registered' || outcome === 'alreadyYours'
}

/**
 * Plockar ut koder ur en fritextruta.
 *
 * Föräldrar klistrar in på alla tänkbara sätt – en per rad, med kommatecken,
 * med mellanslag mellan, med "LB-" kvar eller borttaget. Att kräva ett format
 * vore att lägga arbetet på fel person. Vi delar på allt som inte kan ingå i
 * en kod och låter normalizeCode avgöra vad som är giltigt.
 *
 * Dubbletter faller bort tidigt: skriver någon in samma lapp två gånger är
 * det ett skrivfel, inte två lappar, och andra raden skulle annars komma
 * tillbaka som "Redan din" och se ut som ett fel.
 */
export function parseSlipCodes(input: string): string[] {
  const seen = new Set<string>()
  const out: string[] = []

  for (const chunk of input.split(/[\s,;]+/)) {
    if (!chunk) continue
    // Okända tecken behålls som de är, så att raden går att peka ut i svaret.
    const key = normalizeCode(chunk) ?? chunk.trim().toUpperCase()
    if (seen.has(key)) continue
    seen.add(key)
    out.push(chunk.trim())
  }

  return out
}
